// Napojení nativní aplikace (Capacitor) na router: klepnutí na push a sdílení do Wac Buddy.
// Ve webu se nespustí nic (isNative() je false). Volá se jednou z App.vue.
// Pluginy (@capacitor/app, send-intent) se načítají dynamicky až za isNative(), do webu nejdou.
import type { PluginListenerHandle } from '@capacitor/core'
import type { Router } from 'vue-router'
import { isNative } from './native'
import { listenNativePush } from './push'
import { buddyShareLocation, sharedTextFromIntent } from './share'

/**
 * Po zpracování sdílení zavolat SendIntent.finish() (zavře aktivitu, do které sdílení přišlo)?
 * Vypnuto: filtr ACTION_SEND je na MainActivity (singleTask), takže finish() by po přesměrování
 * zavřel celou aplikaci včetně obrazovky s předvyplněným textem. Opakované doručení téhož sdílení
 * hlídá lastShare; MainActivity.onNewIntent předá nový intent pluginu přes setIntent().
 */
export const FINISH_AFTER_SHARE = false

let lastShare = ''

/** Jen pro testy. */
export function _resetNativeApp(): void {
  lastShare = ''
}

/**
 * Zjistí, jestli aplikaci někdo něco nasdílel, a otevře chat s Buddym s předvyplněným textem.
 * Vrací true, když se přešlo na chat. Zprávu neodesílá, text jen čeká ve vstupu.
 */
export async function checkSharedText(router: Router): Promise<boolean> {
  if (!isNative()) return false
  try {
    // Plugin odmítne (reject), když aplikace nebyla spuštěna sdílením: to není chyba.
    const { SendIntent } = await import('send-intent')
    const intent = await SendIntent.checkSendIntentReceived()
    const text = sharedTextFromIntent(intent)
    // Intent zůstává v aktivitě i po návratu do aplikace; stejný text podruhé nevyplňujeme.
    if (!text || text === lastShare) return false
    lastShare = text
    await router.push(buddyShareLocation(text))
    if (FINISH_AFTER_SHARE) SendIntent.finish()
    return true
  } catch {
    return false
  }
}

export interface NativeAppOptions {
  router: Router
  /** Zpráva dorazila, zatímco je aplikace v popředí (obnovit Dnes a Buddyho). */
  onPushReceived?: () => void
}

/** Spustí nativní posluchače. Vrací funkci, která je odebere. Ve webu nedělá nic. */
export async function initNativeApp(options: NativeAppOptions): Promise<() => void> {
  if (!isNative()) return () => undefined
  const { router } = options

  const offPush = await listenNativePush({
    onOpen: (path) => void router.push(path),
    onReceived: options.onPushReceived,
  })

  const check = () => void checkSharedText(router)
  const handles: PluginListenerHandle[] = []
  try {
    const { App } = await import('@capacitor/app')
    handles.push(await App.addListener('appUrlOpen', check))
    handles.push(await App.addListener('resume', check))
  } catch {
    /* plugin App není k dispozici */
  }

  // Aplikace spuštěná sdílením: počkat na první navigaci, ať ji přesměrování nepřebije.
  void router.isReady().then(check)

  return () => {
    offPush()
    for (const h of handles) void h.remove().catch(() => undefined)
  }
}
